/*
 * /hunting-exam — тренажёр охотминимума по официальному перечню Минприроды
 * (утв. 18.09.2025 № 21/12; сборка — scripts/build-hunting-exam-official.py).
 *
 * Чем он отличается от «ещё одного списка вопросов»:
 *  1. У каждого вопроса — НОРМА, из которой следует ответ: пункт Правил охоты
 *     или часть статьи Закона об охоте с цитатой. Ответ, выученный без нормы,
 *     держится до экзамена; норма — до первой охоты.
 *  2. Ошибки самого перечня не спрятаны. В нём 258 вопросов под 257 номерами,
 *     у четырёх вопросов буква ответа противоречит тексту ответа, у двух вместо
 *     буквы напечатано «#». Засчитывается вариант, который подтверждает норма,
 *     и у вопроса сказано, что в перечне иначе.
 *  3. Экзамен — по регламенту ПП РФ от 15.08.2025 № 1227: 100–200 вопросов,
 *     не меньше минуты на вопрос, балл за верный ответ, порог 75 %, по
 *     истечении времени тест закрывается (пп. 4, 19, 22, 26, 27).
 */

import { makeT } from '../maritime/i18n.js';
import { passNeed, score, noteStat, isMistakeStat, nextUnanswered } from './engine.js';

const B = window.__HUNTEXAM || { T: {}, cfg: {} };
const t = makeT(B.T);
const SLUG = 'hunting-exam';
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
};
const LETTERS = ['а', 'б', 'в'];
const ADVANCE_MS = 320;         // экзамен: ответ переводит к следующему вопросу

let D = null;                   // официальный перечень
let byId = new Map();

const S = {
    mode: 'exam',
    stats: {},                  // id → {s: видел, w: ошибок, k: верных подряд}
    run: null,                  // текущее прохождение
    timerId: 0,
    pending: 0,                 // таймер перехода к следующему вопросу (экзамен)
    listQuery: '',
    listTopic: '',
    listErrata: false,
};

/* ── память ──────────────────────────────────────────────────────────────── */
const load = (k, d) => { try { return window.__toolSettings?.load(SLUG, k, d) ?? d; } catch { return d; } };
const save = (k, v) => { try { window.__toolSettings?.save(SLUG, k, v); } catch { /* приватный режим */ } };

const note = (id, right) => { S.stats[id] = noteStat(S.stats[id], right); };
const isMistake = id => isMistakeStat(S.stats[id]);

/* ── выборки ─────────────────────────────────────────────────────────────── */
function shuffle(a) {
    const r = a.slice();
    for (let i = r.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [r[i], r[j]] = [r[j], r[i]];
    }
    return r;
}
/* Варианты перемешиваются: запомнить «ответ — б» нельзя, только сам ответ. */
const item = q => ({ id: q.id, order: shuffle([0, 1, 2]), given: null });

function topicsChecked() {
    return [...document.querySelectorAll('#heTopics input:checked')].map(x => x.value);
}

/* ── прохождение ─────────────────────────────────────────────────────────── */
function startRun(kind, ids, extra = {}) {
    clearTimeout(S.pending);
    clearInterval(S.timerId);
    S.run = { kind, items: ids.map(id => item(byId.get(id))), startedAt: Date.now(), ...extra, idx: extra.idx || 0 };
    showStage('run');
    $('heTimer').hidden = kind !== 'exam';
    lockTabs(kind === 'exam');
    if (kind === 'exam') {
        tick();
        S.timerId = setInterval(tick, 1000);
    }
    renderQuestion();
    window.__stage?.(kind + '_started');
}

function startExam() {
    const n = Number($('heExamSize').value) || 100;
    const per = Number($('heExamPace').value) || 1;
    const ids = shuffle(D.q.map(q => q.id)).slice(0, Math.min(n, D.q.length));
    startRun('exam', ids, { endsAt: Date.now() + Math.round(ids.length * per * 60000), per });
}

function startTrain() {
    const topics = topicsChecked();
    let pool = D.q.filter(q => topics.includes(q.t));
    const size = $('heTrainSize').value;
    if ($('heWeakFirst').checked) {
        // Сначала то, где ошибались, внутри — случайно; остальное — следом.
        const weak = shuffle(pool.filter(q => isMistake(q.id)));
        const rest = shuffle(pool.filter(q => !isMistake(q.id)));
        pool = weak.concat(rest);
    } else {
        pool = shuffle(pool);
    }
    if (size !== 'all') pool = pool.slice(0, Number(size));
    if (!pool.length) return;
    startRun('train', pool.map(q => q.id));
}

function marathonState() {
    const m = load('marathon', null);
    if (m && Array.isArray(m.order) && m.order.length === D.q.length) return m;
    return null;
}
function startMarathon(fresh) {
    let m = fresh ? null : marathonState();
    // Пройденный до конца марафон начинается заново, а не с последнего вопроса.
    if (!m || m.pos >= m.order.length) {
        m = { order: shuffle(D.q.map(q => q.id)), pos: 0, right: 0 };
        save('marathon', m);
    }
    startRun('marathon', m.order, { idx: m.pos });
}

function startMistakes() {
    const ids = shuffle(D.q.filter(q => isMistake(q.id)).map(q => q.id));
    if (!ids.length) return;
    startRun('mistakes', ids);
}

function tick() {
    const left = Math.max(0, S.run.endsAt - Date.now());
    // Часы собираются в коде, не шаблоном перевода: `:m:s` съедает двоеточие
    // (public/js/maritime/i18n.js).
    const h = Math.floor(left / 3600000);
    const m = Math.floor((left % 3600000) / 60000);
    const s = Math.floor((left % 60000) / 1000);
    $('heTimer').textContent = (h ? h + ':' + String(m).padStart(2, '0') : String(m).padStart(2, '0')) + ':' + String(s).padStart(2, '0');
    if (left <= 0) {
        clearInterval(S.timerId);
        finishRun(true);
    }
}

/* ── вопрос ──────────────────────────────────────────────────────────────── */
function renderQuestion() {
    clearTimeout(S.pending);
    const r = S.run;
    const it = r.items[r.idx];
    const q = byId.get(it.id);
    const exam = r.kind === 'exam';

    $('heProgress').textContent = r.kind === 'marathon'
        ? t('progress_marathon', { n: r.idx + 1, total: r.items.length })
        : t('progress', { n: r.idx + 1, total: r.items.length });
    $('heTopic').textContent = t('topic_' + q.t);
    $('heNum').textContent = q.n.endsWith('-2') ? t('num_dup', { n: q.n.slice(0, -2) }) : t('num', { n: q.n });
    $('heQ').textContent = q.q;

    const box = $('heAnswers');
    box.textContent = '';
    // Метка стража «один жест — один ответ» стоит и в блейде; здесь — на случай,
    // если контейнер ответов когда-нибудь будет строиться скриптом.
    box.setAttribute('data-answer', '');
    it.order.forEach((oi, pos) => {
        const b = el('button', 'he-ans');
        b.type = 'button';
        b.append(el('span', 'he-ans-n', String(pos + 1)));
        b.append(el('span', null, q.a[oi]));
        if (exam) {
            // На экзамене ответ не подсвечивается верным или неверным — как на
            // настоящей проверке, где результат объявляют после теста.
            if (it.given === oi) b.classList.add('pick');
            b.addEventListener('click', () => examPick(oi));
        } else if (it.given !== null) {
            b.disabled = true;
            if (oi === q.c) b.classList.add('ok');
            else if (oi === it.given) b.classList.add('no');
            else b.classList.add('dim');
        } else {
            b.addEventListener('click', () => trainAnswer(oi));
        }
        box.append(b);
    });

    const why = $('heWhy');
    why.textContent = '';
    if (!exam && it.given !== null) {
        why.hidden = false;
        why.className = 'he-why ' + (it.given === q.c ? 'ok' : 'no');
        why.append(el('div', 'he-why-h', it.given === q.c ? t('right') : t('wrong')));
        if (it.given !== q.c) why.append(el('p', 'he-correct', t('correct_is', { a: q.a[q.c] })));
        why.append(basis(q));
    } else {
        why.hidden = true;
    }
    // Пометка об ошибке перечня — только после ответа и не на экзамене:
    // у вопросов с перепутанной буквой она называет верный вариант.
    const err = errataNote(q);
    $('heErrata').hidden = !(err && !exam && it.given !== null);
    $('heErrata').textContent = err || '';
    $('heConfirm').hidden = true;

    $('hePrev').disabled = r.idx === 0;
    $('heNext').textContent = r.idx === r.items.length - 1 ? t('finish') : t('next');
    $('heNext').disabled = !exam && it.given === null;
    renderDots();
}

function renderDots() {
    const r = S.run;
    const d = $('heDots');
    d.textContent = '';
    d.hidden = r.kind !== 'exam';
    if (r.kind !== 'exam') return;
    const frag = document.createDocumentFragment();
    r.items.forEach((it, i) => {
        const b = el('button', 'he-dot' + (it.given !== null ? ' done' : '') + (i === r.idx ? ' cur' : ''), String(i + 1));
        b.type = 'button';
        b.setAttribute('aria-label', t('goto', { n: i + 1 }));
        b.addEventListener('click', () => { S.run.idx = i; renderQuestion(); });
        frag.append(b);
    });
    d.append(frag);
}

/* Экзамен: ответ переводит к следующему неотвеченному вопросу. Второй клик
   за время перехода ЗАМЕНЯЕТ ждущий ответ, а не отвечает на следующий
   вопрос (общий страж data-answer гасит сам двойной клик и зажатую клавишу,
   docs/why/primitives/one-gesture-one-answer.md). */
function examPick(oi) {
    const r = S.run;
    const it = r.items[r.idx];
    it.given = oi;
    for (const b of $('heAnswers').children) b.classList.remove('pick');
    const pos = it.order.indexOf(oi);
    $('heAnswers').children[pos]?.classList.add('pick');
    renderDots();
    clearTimeout(S.pending);
    S.pending = setTimeout(() => {
        const next = nextUnanswered(r.items, r.idx);
        if (next !== -1) { r.idx = next; renderQuestion(); }
    }, ADVANCE_MS);
}

function trainAnswer(oi) {
    const r = S.run;
    const it = r.items[r.idx];
    if (it.given !== null) return;
    it.given = oi;
    const q = byId.get(it.id);
    const right = oi === q.c;
    note(q.id, right);
    save('stats', S.stats);
    if (r.kind === 'marathon') {
        const m = marathonState();
        if (m) { m.pos = r.idx + 1; if (right) m.right++; save('marathon', m); }
    }
    renderQuestion();
    window.__aeFunnel?.('answer', { mode: r.kind, topic: q.t, correct: right });
    // Фокус — на «Дальше», чтобы следующий шаг был той же клавишей Enter.
    try { $('heNext').focus({ preventScroll: true }); } catch { /* старый браузер */ }
}

function errataNote(q) {
    if (!q.e) return '';
    if (q.e.k === 'letter') {
        const printed = q.a[LETTERS.indexOf(q.e.p)];
        return t('err_letter', { printed, right: q.a[q.c] });
    }
    return t('err_' + q.e.k);
}

function basis(q) {
    const wrap = el('div', 'he-basis');
    if (!q.r.length) {
        wrap.append(el('p', 'he-basis-none', t('basis_none')));
        return wrap;
    }
    q.r.forEach((key, i) => {
        const ref = D.refs[key];
        const host = i === 0 ? wrap : (wrap.querySelector('details') || (() => {
            const det = el('details', 'he-more');
            det.append(el('summary', null, t('basis_more', { n: q.r.length - 1 })));
            wrap.append(det);
            return det;
        })());
        const head = el('div', 'he-basis-h');
        head.append(el('span', null, t('basis') + ' '));
        const a = el('a', null, ref.l);
        a.href = ref.u;
        a.target = '_blank';
        a.rel = 'noopener';
        head.append(a);
        host.append(head, el('blockquote', 'he-quote', ref.t));
    });
    return wrap;
}

/* На экзамене неотвеченный вопрос — ноль баллов (ПП № 1227 п. 26), поэтому
   «Завершить» с пропусками переспрашивает один раз, а не закрывает тест молча. */
function confirmFinish() {
    const r = S.run;
    if (!r) return;
    const left = r.items.filter(it => it.given === null).length;
    if (r.kind === 'exam' && left && !r.confirm) {
        r.confirm = true;
        $('heConfirm').hidden = false;
        $('heConfirm').textContent = t('exam_unanswered', { n: left });
        return;
    }
    finishRun();
}

/* ── итог ────────────────────────────────────────────────────────────────── */
function finishRun(byTimer = false) {
    clearTimeout(S.pending);
    clearInterval(S.timerId);
    const r = S.run;
    if (!r) return;
    r.done = true;
    lockTabs(false);
    const exam = r.kind === 'exam';
    if (exam) {
        // Счёт — только теперь: на экзамене разбора по ходу нет.
        for (const it of r.items) note(it.id, it.given === byId.get(it.id).c);
        save('stats', S.stats);
    }
    const answered = r.items.filter(it => it.given !== null);
    const sc = score(r.items, id => byId.get(id).c);
    const right = sc.right;
    const total = exam ? r.items.length : answered.length;
    const need = passNeed(r.items.length);
    const passed = exam ? sc.passed : null;

    showStage('result');
    const box = $('heResult');
    box.textContent = '';
    box.className = 'tc-panel he-result' + (exam ? (passed ? ' ok' : ' no') : '');

    if (exam) {
        box.append(el('div', 'he-res-h', passed ? t('res_pass') : t('res_fail')));
        const pct = Math.round(right / r.items.length * 100);
        box.append(el('div', 'he-res-s', t('res_exam', { right, total: r.items.length, pct, need })));
        if (byTimer) box.append(el('p', 'tc-note', t('res_timeout')));
        const used = Math.round((Date.now() - r.startedAt) / 60000);
        box.append(el('p', 'tc-note', t('res_time', { used, limit: Math.round(r.items.length * r.per) })));
        const hist = load('attempts', []);
        // Время попытки — меткой, а дата — по часам человека при показе:
        // toISOString() дал бы вчерашнюю дату всему, что восточнее Гринвича.
        hist.unshift({ d: Date.now(), n: r.items.length, r: right, p: passed ? 1 : 0 });
        save('attempts', hist.slice(0, 10));
        renderAttempts();
        window.__aeToolComplete?.({ passed, score: right, total: r.items.length });
    } else if (r.kind === 'marathon') {
        const m = marathonState();
        box.append(el('div', 'he-res-h', t('res_marathon', { n: m ? Math.min(m.pos, D.q.length) : 0, total: D.q.length, right: m ? m.right : 0 })));
    } else {
        box.append(el('div', 'he-res-h', t('res_set', { right, total })));
    }

    const wrong = r.items.filter(it => it.given !== byId.get(it.id).c && (exam || it.given !== null));
    if (wrong.length) {
        const byT = {};
        for (const it of wrong) { const q = byId.get(it.id); (byT[q.t] ||= []).push(q); }
        box.append(el('div', 'he-sub', t('res_weak')));
        const stats = el('div', 'he-weak');
        for (const [topic, list] of Object.entries(byT).sort((a, b) => b[1].length - a[1].length)) {
            const row = el('div', 'he-weakrow');
            row.append(el('span', 'he-weak-t', t('topic_' + topic)));
            row.append(el('span', 'he-weak-n', t('res_weak_n', { n: list.length })));
            stats.append(row);
        }
        box.append(stats);
        box.append(el('div', 'he-sub', t('res_mistakes')));
        for (const it of wrong) {
            const q = byId.get(it.id);
            const det = el('details', 'he-card');
            const sum = el('summary');
            sum.append(el('span', 'he-card-n', q.n.replace('-2', '')));
            sum.append(el('span', null, q.q));
            det.append(sum);
            if (it.given !== null) det.append(el('p', 'he-given', t('your_answer', { a: q.a[it.given] })));
            else det.append(el('p', 'he-given', t('no_answer')));
            det.append(el('p', 'he-correct', t('correct_is', { a: q.a[q.c] })));
            const err = errataNote(q);
            if (err) det.append(el('p', 'he-errata', err));
            det.append(basis(q));
            box.append(det);
        }
    }

    const acts = el('div', 'tc-actions');
    const again = el('button', 'tc-btn', exam ? t('again_exam') : t('again'));
    again.type = 'button';
    again.addEventListener('click', () => {
        if (r.kind === 'exam') startExam();
        else if (r.kind === 'mistakes') { renderMistakesInfo(); startMistakes(); }
        else if (r.kind === 'marathon') startMarathon(false);
        else startTrain();
    });
    acts.append(again);
    // Итог стоит на месте панели режима; вернуться к её настройкам — отсюда
    // или той же вкладкой.
    const setup = el('button', 'tc-btn ghost', t('to_setup'));
    setup.type = 'button';
    setup.addEventListener('click', () => setMode(S.mode));
    acts.append(setup);
    if (S.mode !== 'mistakes' && D.q.some(q => isMistake(q.id))) {
        const go = el('button', 'tc-btn ghost', t('go_mistakes'));
        go.type = 'button';
        go.addEventListener('click', () => setMode('mistakes'));
        acts.append(go);
    }
    box.append(acts);
    renderMistakesInfo();
    renderMarathonInfo();
    S.run = null;
}

/* ── рейка ───────────────────────────────────────────────────────────────── */
/* Темы, их счёт, размеры экзамена и строка источника нарисованы блейдом из
   того же файла перечня (первый экран — в разметке, docs/why/primitives/
   early-action.md); скрипт дописывает только то, что знает браузер. */
function updateTrainInfo() {
    const n = D.q.filter(q => topicsChecked().includes(q.t)).length;
    $('heTrainInfo').textContent = t('available', { n });
    $('heTrainStart').disabled = n === 0;
}
function updateExamInfo() {
    const n = Number($('heExamSize').value) || 100;
    const per = Number($('heExamPace').value) || 1;
    $('heExamInfo').textContent = t('exam_info', { n, min: Math.round(n * per), need: passNeed(n) });
}
function renderMarathonInfo() {
    const m = marathonState();
    const pos = m ? Math.min(m.pos, D.q.length) : 0;
    $('heMarathonInfo').textContent = t('marathon_info', { n: pos, total: D.q.length, right: m ? m.right : 0 });
    $('heMarathonGo').textContent = pos > 0 && pos < D.q.length ? t('marathon_continue') : t('marathon_start');
    $('heMarathonReset').hidden = !(pos > 0);
}
function renderMistakesInfo() {
    const n = D.q.filter(q => isMistake(q.id)).length;
    $('heMistakesInfo').textContent = n ? t('mistakes_info', { n }) : t('mistakes_none');
    $('heMistakesGo').disabled = n === 0;
}
function renderAttempts() {
    const box = $('heAttempts');
    const hist = load('attempts', []);
    box.textContent = '';
    box.hidden = !hist.length;
    if (!hist.length) return;
    box.append(el('div', 'tc-eyebrow', t('attempts_title')));
    let fmt = null;
    try { fmt = new Intl.DateTimeFormat(window.__intlLocale ? window.__intlLocale() : 'ru', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { /* старый браузер */ }
    for (const h of hist.slice(0, 5)) {
        const row = el('div', 'he-attempt ' + (h.p ? 'ok' : 'no'));
        const when = typeof h.d === 'number' && fmt ? fmt.format(new Date(h.d)) : String(h.d);
        row.append(el('span', null, when), el('span', null, t('attempt_row', { r: h.r, n: h.n, pct: Math.round(h.r / h.n * 100) })));
        box.append(row);
    }
}

/* ── перечень целиком ────────────────────────────────────────────────────── */
function renderList() {
    const host = $('heList');
    host.textContent = '';
    // Поиск и фильтры читаются из полей: напечатанное до скрипта не теряется.
    S.listQuery = $('heListQ').value;
    S.listTopic = $('heListTopic').value;
    S.listErrata = $('heListErrata').checked;
    const query = S.listQuery.trim().toLowerCase();
    const list = D.q.filter(q =>
        (!S.listTopic || q.t === S.listTopic) &&
        (!S.listErrata || q.e) &&
        (!query || q.q.toLowerCase().includes(query) || q.a.some(a => a.toLowerCase().includes(query))));
    host.append(el('p', 'tc-note', t('list_found', { n: list.length, total: D.q.length })));
    const LIMIT = 40;
    const draw = q => {
        const det = el('details', 'he-card');
        const sum = el('summary');
        sum.append(el('span', 'he-card-n', q.n.replace('-2', '')));
        sum.append(el('span', null, q.q));
        if (q.e) sum.append(el('span', 'he-badge', t('badge_errata')));
        if (isMistake(q.id)) sum.append(el('span', 'he-badge warn', t('badge_mistake')));
        det.append(sum);
        const ul = el('ul', 'he-opts');
        q.a.forEach((a, i) => ul.append(el('li', i === q.c ? 'correct' : '', LETTERS[i] + ') ' + a)));
        det.append(ul);
        const err = errataNote(q);
        if (err) det.append(el('p', 'he-errata', err));
        det.append(basis(q));
        return det;
    };
    const wrap = el('div');
    list.slice(0, LIMIT).forEach(q => wrap.append(draw(q)));
    host.append(wrap);
    if (list.length > LIMIT) {
        const more = el('button', 'tc-btn ghost', t('list_more', { n: list.length - LIMIT }));
        more.type = 'button';
        more.addEventListener('click', () => { more.remove(); list.slice(LIMIT).forEach(q => wrap.append(draw(q))); });
        host.append(more);
    }
}

/* ── режимы ──────────────────────────────────────────────────────────────── */
function setMode(m) {
    // Идущий экзамен вкладкой не прерывается (вкладки заперты, см. lockTabs);
    // тренировку или марафон можно оставить — ответы уже в памяти.
    if (S.run && S.run.kind !== 'exam') {
        clearTimeout(S.pending);
        S.run = null;
    }
    S.mode = m;
    for (const b of document.querySelectorAll('.he-tab')) {
        const on = b.dataset.mode === m;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (const p of document.querySelectorAll('.he-setup')) p.hidden = p.dataset.mode !== m;
    // Под вкладками — панель режима; прохождение и итог встают на её место
    // (showStage), вкладка возвращает панель.
    if (!S.run) showStage('setup');
    $('heStageList').hidden = m !== 'list';
    if (m === 'list') renderList();
    if (m === 'mistakes') renderMistakesInfo();
    if (m === 'marathon') renderMarathonInfo();
    window.__stage?.('mode_' + m);
}

/** Что стоит под вкладками: панель режима, вопрос или итог. */
function showStage(what) {
    $('heSetups').hidden = what !== 'setup';
    $('heRun').hidden = what !== 'run';
    $('heResult').hidden = what !== 'result';
    if (what !== 'setup') $('heStageList').hidden = true;
}

/* На экзамене таймер не ждёт, поэтому уйти на другую вкладку посреди теста
   нельзя: случайный клик не должен ни прервать тест, ни оставить его тикать
   за кадром. Вкладки отпираются, когда тест закончен. */
function lockTabs(on) {
    for (const b of document.querySelectorAll('.he-tab')) b.disabled = on && b.dataset.mode !== 'exam';
}

function bind() {
    for (const b of document.querySelectorAll('.he-tab')) b.addEventListener('click', () => setMode(b.dataset.mode));
    $('heExamStart').addEventListener('click', startExam);
    $('heExamSize').addEventListener('change', updateExamInfo);
    $('heExamPace').addEventListener('change', updateExamInfo);
    $('heTrainStart').addEventListener('click', startTrain);
    $('heMarathonGo').addEventListener('click', () => startMarathon(false));
    $('heMarathonReset').addEventListener('click', () => { save('marathon', null); renderMarathonInfo(); startMarathon(true); });
    $('heMistakesGo').addEventListener('click', startMistakes);
    $('hePrev').addEventListener('click', () => { if (S.run && S.run.idx > 0) { S.run.idx--; renderQuestion(); } });
    $('heNext').addEventListener('click', () => {
        const r = S.run;
        if (!r) return;
        if (r.idx < r.items.length - 1) { r.idx++; renderQuestion(); }
        else confirmFinish();
    });
    $('heStop').addEventListener('click', confirmFinish);
    for (const cb of document.querySelectorAll('#heTopics input')) cb.addEventListener('change', updateTrainInfo);
    let deb = 0;
    $('heListQ').addEventListener('input', () => {
        clearTimeout(deb);
        deb = setTimeout(renderList, 200);
    });
    $('heListTopic').addEventListener('change', renderList);
    $('heListErrata').addEventListener('change', renderList);
    document.addEventListener('keydown', e => {
        if (!S.run || $('heRun').hidden) return;
        if (window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : e.target.matches('input,select,textarea,button')) return;
        const it = S.run.items[S.run.idx];
        if (e.key >= '1' && e.key <= '3') {
            const b = $('heAnswers').children[Number(e.key) - 1];
            if (b && !b.disabled) { b.click(); e.preventDefault(); }
        } else if (e.key === 'Enter' && (S.run.kind === 'exam' || it.given !== null)) {
            $('heNext').click();
            e.preventDefault();
        }
    });
}

/* Нажатие на первом экране, сделанное до загрузки скрипта и перечня, держит
   шим __earlyAction из <head>; здесь оно выполняется тем же путём, что живой
   клик. Звать — когда перечень загружен, в той же задаче, что bind(): иначе
   клик между ними выполнился бы дважды. */
function replayEarly(act) {
    if (act.startsWith('tab:')) { setMode(act.slice(4)); return; }
    const mode = { exam: 'exam', train: 'train', marathon: 'marathon', mistakes: 'mistakes' }[act];
    if (!mode) return;
    setMode(mode);
    ({ exam: startExam, train: startTrain, marathon: () => startMarathon(false), mistakes: startMistakes })[mode]();
}

async function boot() {
    try {
        const r = await fetch(B.cfg.dataUrl, { credentials: 'same-origin' });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        D = await r.json();
        byId = new Map(D.q.map(q => [q.id, q]));
        const st = load('stats', {});
        S.stats = st && typeof st === 'object' ? st : {};
        bind();
        updateTrainInfo();
        updateExamInfo();
        renderAttempts();
        renderMarathonInfo();
        renderMistakesInfo();
        setMode('exam');
        if (window.__earlyAction) window.__earlyAction.claim('hunting-exam', replayEarly);
        window.__stage?.('ready');
    } catch (err) {
        // Отказ — тоже claim, но без действия: иначе кнопка крутится вечно.
        if (window.__earlyAction) window.__earlyAction.claim('hunting-exam');
        window.__handleError?.(err, 'hunting-exam-load', { say: t('load_error') });
        const l = $('heLoadError');
        if (l) { l.hidden = false; l.textContent = t('load_error'); }
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
